import { useEffect, useState, type FormEvent } from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import {
  isIdentifierGuardError,
  roundsErrorMessage,
  useCreateRoundThread,
  useRoundsCommunities,
  useRoundsStatus,
} from "@/lib/rounds";

export default function RoundsNew() {
  const { city } = useCity();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedCommunity = searchParams.get("community") ?? "";
  const communities = useRoundsCommunities(city.slug);
  const status = useRoundsStatus(city.slug);
  const createThread = useCreateRoundThread(city.slug);
  const [communitySlug, setCommunitySlug] = useState(requestedCommunity);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [isAnonymisedCase, setIsAnonymisedCase] = useState(false);
  const [declaration, setDeclaration] = useState(false);
  const identifierError = isIdentifierGuardError(createThread.error);

  useEffect(() => {
    const options = communities.data?.communities ?? [];
    if (!options.length) return;
    if (options.some((item) => item.slug === communitySlug)) return;
    const requested = options.find((item) => item.slug === requestedCommunity);
    setCommunitySlug(requested?.slug ?? options[0]?.slug ?? "");
  }, [communities.data, communitySlug, requestedCommunity]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!communitySlug || !title.trim() || !body.trim()) return;
    createThread.mutate(
      {
        communitySlug,
        title: title.trim(),
        body: body.trim(),
        isAnonymisedCase,
        declaration: isAnonymisedCase ? declaration : false,
        tags: [],
      },
      {
        onSuccess: (thread) => {
          track("thread_create", { thread_id: thread.id });
          navigate(`/board/rounds/t/${encodeURIComponent(thread.id)}`);
        },
      },
    );
  };

  return (
    <section className="pb-rounds-new" aria-labelledby="rounds-new-heading">
      <Link className="pb-rounds-back" to="/board/rounds"><ArrowLeft size={13} aria-hidden="true" />Back to Rounds</Link>
      <header>
        <p className="pb-board-eyebrow">Start a discussion</p>
        <h1 id="rounds-new-heading">New Rounds thread</h1>
        <p>Write for clinical peers, and remove every detail that could identify a patient.</p>
      </header>

      {status.data && !status.data.handleSet ? (
        <p className="pb-rounds-setup-note" role="alert">
          Add your member handle in <Link to="/board/settings">Board settings</Link> before posting.
        </p>
      ) : null}

      <form onSubmit={submit}>
        <label>
          Community
          <select
            required
            value={communitySlug}
            disabled={communities.isLoading || communities.isError}
            onChange={(event) => setCommunitySlug(event.target.value)}
          >
            <option value="" disabled>Select a community</option>
            {communities.data?.communities.map((community) => (
              <option key={community.id} value={community.slug}>{community.name}</option>
            ))}
          </select>
        </label>
        <label>
          Title
          <input
            required
            maxLength={200}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
          <small>{title.length} / 200</small>
        </label>
        <label>
          Body
          <textarea
            required
            maxLength={8000}
            value={body}
            aria-describedby={identifierError ? "rounds-new-identifier-error" : undefined}
            onChange={(event) => {
              setBody(event.target.value);
              if (createThread.isError) createThread.reset();
            }}
          />
          {identifierError ? (
            <span className="pb-rounds-identifier-error" id="rounds-new-identifier-error" role="alert">
              Remove patient-identifying details before posting. <Link to="/board/rounds/guidelines">Read the case-writing guidelines.</Link>
            </span>
          ) : null}
          <small>{body.length.toLocaleString("en-AU")} / 8,000</small>
        </label>

        <label className="pb-rounds-toggle-row">
          <input
            type="checkbox"
            checked={isAnonymisedCase}
            onChange={(event) => {
              setIsAnonymisedCase(event.target.checked);
              if (!event.target.checked) setDeclaration(false);
            }}
          />
          <span><strong>This is an anonymised case</strong><small>Applies stricter identifier checks and displays a case badge.</small></span>
        </label>

        {isAnonymisedCase ? (
          <aside className="pb-rounds-case-rules">
            <ShieldCheck size={18} aria-hidden="true" />
            <div>
              <strong>Case-writing rules</strong>
              <p>No dates, ages over 89, suburbs, names, employers, identifiers, or rare combinations.</p>
              <label>
                <input
                  type="checkbox"
                  required
                  checked={declaration}
                  onChange={(event) => setDeclaration(event.target.checked)}
                />
                I have removed patient-identifying information.
              </label>
            </div>
          </aside>
        ) : null}

        {!identifierError && createThread.isError ? (
          <p className="pb-rounds-form-error" role="alert">{roundsErrorMessage(createThread.error)}</p>
        ) : null}
        {communities.isError ? <p className="pb-rounds-form-error" role="alert">Communities could not be loaded.</p> : null}

        <footer>
          <Link to="/board/rounds">Cancel</Link>
          <button
            className="pb-rounds-primary"
            type="submit"
            disabled={
              createThread.isPending ||
              !status.data?.handleSet ||
              !communitySlug ||
              !title.trim() ||
              !body.trim() ||
              (isAnonymisedCase && !declaration)
            }
          >
            {createThread.isPending ? "Posting…" : "Post thread"}
          </button>
        </footer>
      </form>
    </section>
  );
}
